/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { apiFetchAuth } from '../../api/client';
import { useMarket } from '../../payments/useMarket';
import { prepareImageUploadAsset } from '../../utils/imageUpload';
import { trackEvent } from '../../utils/analytics';
import './StyleCheck.css';
import { anchoredItems, normalizedAction, percentLabel, scoreLabel } from './styleCheckResult';
import StyleRecommendationSections, { CurrentStyleHeading } from './StyleRecommendationSections';
import StyleDiscoverySection from './discovery/StyleDiscoverySection';
import { buildStyleBetaSurveyUrl, getOrCreateStyleBetaSessionId, getResultBand, getStyleBetaSurveyAnalyticsParams, getStyleBetaUtms, isStyleBetaSession, STYLE_BETA_EXPERIMENT } from './styleBetaSurvey';
const EVALUATIONS = ['outfit_coherence', 'personal_fit', 'goal_fit'];
const ANALYSIS_STEPS = ['silhouette', 'palette', 'harmony', 'recommendation'];

function StyleAnalysisLoading({ imageSrc, t }) {
  return (
    <section className="style-analysis-loading" role="status" aria-live="polite" aria-label={t('style_check.analysis.aria_label')}>
      <div className="style-analysis-visual" aria-hidden="true">
        <img src={imageSrc} alt="" />
        <div className="style-analysis-vignette" /><div className="style-analysis-grid" /><div className="style-analysis-scan"><span /></div>
        <i className="style-analysis-point point-one" /><i className="style-analysis-point point-two" /><i className="style-analysis-point point-three" />
        <div className="style-analysis-label label-fit">{t('style_check.analysis.labels.fit')}</div><div className="style-analysis-label label-tone">{t('style_check.analysis.labels.tone')}</div>
      </div>
      <div className="style-analysis-copy">
        <p className="style-analysis-eyebrow"><span /> {t('style_check.analysis.eyebrow')}</p>
        <h2>{t('style_check.analysis.title')}</h2>
        <div className="style-analysis-current">{ANALYSIS_STEPS.map((step) => <p key={step}>{t('style_check.analysis.steps.' + step + '.detail')}</p>)}</div>
        <ol className="style-analysis-steps">
          {ANALYSIS_STEPS.map((step, index) => (
            <li key={step}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><strong>{t('style_check.analysis.steps.' + step + '.title')}</strong><small>{t('style_check.analysis.steps.' + step + '.caption')}</small></div>
            </li>
          ))}
        </ol>
        <p className="style-analysis-note">{t('style_check.analysis.note')}</p>
      </div>
    </section>
  );
}

function Replacement({ replacement, t }) {
  if (!replacement) return null;
  const rows = [
    ['category', replacement.category], ['colors', replacement.colors?.join(', ')],
    ['shape', replacement.shape], ['material', replacement.material], ['mood', replacement.mood?.join(', ')],
  ].filter(([, value]) => value);
  return (
    <div className="style-check-replacement">
      <strong>{t('style_check.result.replacement')}</strong>
      {rows.map(([key, value]) => <p key={key}><span>{t(`style_check.item.${key}`)}</span>{value}</p>)}
    </div>
  );
}

function EvaluationRow({ type, evaluation, t }) {
  if (!evaluation) return null;
  const status = ['GOOD', 'MODERATE', 'LOW', 'UNKNOWN'].includes(evaluation.status) ? evaluation.status : 'UNKNOWN';
  return (
    <div className="style-check-evaluation">
      <div><strong>{t(`style_check.evaluations.${type}`)}</strong><span className={`status-${status.toLowerCase()}`}>{t(`style_check.statuses.${status}`)}</span><b>{scoreLabel(evaluation.score)}</b></div>
      <p>{evaluation.reason}</p>
    </div>
  );
}

function ItemCard({ item, index, active, onSelect, cardRef, t }) {
  const action = normalizedAction(item);
  const uncertain = item.confidence < 70;
  return (
    <article ref={cardRef} className={`style-check-item ${active ? 'is-active' : ''}`} onClick={onSelect} tabIndex="0" onFocus={onSelect}>
      <div className="style-check-item-head">
        <div><p className="style-check-category">{index + 1} · {t(`style_check.categories.${item.category}`, item.category)}</p><h3>{item.name}</h3></div>
        <div className="style-check-confidence"><span>{t('style_check.result.confidence')}</span><strong>{percentLabel(item.confidence)}</strong></div>
      </div>
      <span className={`style-check-action action-${action.toLowerCase()}`}>{uncertain ? t('style_check.actions.UNCERTAIN') : t(`style_check.actions.${action}`)}</span>
      {EVALUATIONS.map((type) => <EvaluationRow key={type} type={type} evaluation={item[type]} t={t} />)}
      <p className="style-check-reason">{item.final_reason}</p>
      {item.adjustment && <p className="style-check-adjustment"><strong>{t('style_check.result.adjustment')}</strong>{item.adjustment}</p>}
      <Replacement replacement={item.replacement} t={t} />
    </article>
  );
}

function OutfitPhoto({ src, items, activeIndex, onSelect, t }) {
  const markers = anchoredItems(items);
  return (
    <div className="style-check-photo-panel">
      <div className="style-check-image-stage">
        <img src={src} alt={t('style_check.upload.preview_alt')} />
        <div className="style-check-markers" aria-label={t('style_check.result.item_positions')}>
          {markers.map(({ item, index, style }) => {
            const action = normalizedAction(item);
            return (
              <button key={`marker-${index}`} type="button" className={`style-check-marker marker-${action.toLowerCase()} ${activeIndex === index ? 'is-active' : ''}`}
                style={style} onClick={() => onSelect(index, true)} onMouseEnter={() => onSelect(index, false)}
                aria-label={`${index + 1}. ${item.name}: ${t(`style_check.actions.${item.confidence < 70 ? 'UNCERTAIN' : action}`)}`}>
                <span>{index + 1}</span><em>{t(`style_check.marker_actions.${item.confidence < 70 ? 'UNCERTAIN' : action}`)}</em>
              </button>
            );
          })}
        </div>
      </div>
      <p className="style-check-position-note">{t('style_check.result.position_note')}</p>
    </div>
  );
}

const compactCopy = (value, limit = 90) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text.length <= limit) return text;
  const sentence = text.slice(0, limit + 1).match(/^.*?[.!?。](?:\s|$)/)?.[0];
  return sentence || text.slice(0, limit).trimEnd() + '…';
};

function ResultQuickActions({ result, t }) {
  const items = Array.isArray(result?.items) ? result.items : [];
  const keep = items.filter((item) => normalizedAction(item) === 'KEEP').sort((a, b) => (b.confidence || 0) - (a.confidence || 0))[0];
  const change = items.find((item) => ['ADJUST', 'REPLACE', 'REMOVE'].includes(normalizedAction(item)) && (item.adjustment || item.final_reason));
  const actions = [
    { key: 'keep', label: t('style_check.quick_actions.keep'), title: keep?.name || t('style_check.quick_actions.balance'), body: keep?.final_reason || result?.overall_summary },
    { key: 'priority', label: t('style_check.quick_actions.priority'), title: t('style_check.quick_actions.first_move'), body: result?.priority_tip },
    { key: 'change', label: t('style_check.quick_actions.change'), title: change?.name || t('style_check.quick_actions.detail'), body: change?.adjustment || change?.final_reason },
  ].filter((action) => action.body);
  if (!actions.length) return null;
  return (
    <section className="style-quick-actions" aria-labelledby="style-quick-actions-title">
      <div className="style-quick-actions-heading"><p>{t('style_check.quick_actions.eyebrow')}</p><h2 id="style-quick-actions-title">{t('style_check.quick_actions.title')}</h2></div>
      <div>{actions.map((action, index) => <article key={action.key}><span>0{index + 1} · {action.label}</span><h3>{action.title}</h3><p>{action.body}</p></article>)}</div>
      <a href="#style-check-details">{t('style_check.quick_actions.details')}</a>
    </section>
  );
}

function SurveyBenefitCard({ surveyUrl, placement, onClick, t }) {
  const compact = placement === 'result_bottom';
  return (
    <aside className={`style-survey-benefit ${compact ? 'is-compact' : ''}`} aria-labelledby={`style-survey-${placement}-title`}>
      <div className="style-survey-benefit-copy">
        <p className="style-survey-benefit-badge">{t('style_check.survey.badge')}</p>
        <h2 id={`style-survey-${placement}-title`}>{t(`style_check.survey.${compact ? 'bottom_title' : 'title'}`)}</h2>
        <p>{t(`style_check.survey.${compact ? 'bottom_body' : 'body'}`)}</p>
      </div>
      {!compact && <ul>
        {['hair', 'items', 'outfits', 'new_features'].map((benefit) => <li key={benefit}><span>✓</span>{t(`style_check.survey.benefits.${benefit}`)}</li>)}
      </ul>}
      <div className="style-survey-benefit-action">
        <a href={surveyUrl} onClick={() => onClick(placement)}>{t('style_check.survey.cta')}</a>
        <small>{t('style_check.survey.note')}</small>
      </div>
    </aside>
  );
}

export default function StyleCheck() {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const { loading: marketLoading, data: marketData, error: marketError } = useMarket();
  const [asset, setAsset] = useState(null);
  const [faceAsset, setFaceAsset] = useState(null);
  const [recommendationMode, setRecommendationMode] = useState('neutral');
  const [goal, setGoal] = useState('');
  const [occasion, setOccasion] = useState('');
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errorCode, setErrorCode] = useState('');
  const [rewardClaim, setRewardClaim] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef([]);
  const goals = useMemo(() => ['minimal', 'urban', 'soft', 'classic', 'bold'], []);
  const occasions = useMemo(() => ['daily', 'work', 'date', 'formal', 'travel'], []);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const betaSession = useMemo(() => isStyleBetaSession(searchParams), [searchParams]);
  const marketResolved = !marketLoading && !marketError && ['KR', 'GLOBAL'].includes(marketData?.market);
  const safeMarket = marketResolved ? marketData.market : null;
  const betaUtms = useMemo(() => getStyleBetaUtms(searchParams), [searchParams]);
  const resultBand = getResultBand(result?.overall_score);
  useEffect(() => {
    if (!result || !betaSession) { setRewardClaim(''); return undefined; }
    let cancelled = false;
    const betaSessionId = getOrCreateStyleBetaSessionId();
    apiFetchAuth('/api/style-beta/reward-claims', {
      method: 'POST', body: JSON.stringify({ beta_session_id: betaSessionId }),
    }).then((payload) => {
      if (!cancelled) setRewardClaim(payload?.reward_claim || '');
    }).catch(() => { if (!cancelled) setRewardClaim(''); });
    return () => { cancelled = true; };
  }, [betaSession, result]);
  const surveyUrl = useMemo(() => {
    if (!result || !betaSession || !rewardClaim || !import.meta.env.VITE_STYLE_BETA_SURVEY_URL) return null;
    return buildStyleBetaSurveyUrl({
      baseUrl: import.meta.env.VITE_STYLE_BETA_SURVEY_URL, rewardClaim, betaSessionId: getOrCreateStyleBetaSessionId(),
      experimentId: STYLE_BETA_EXPERIMENT, utmSource: betaUtms.utm_source, utmMedium: betaUtms.utm_medium,
      utmCampaign: betaUtms.utm_campaign, utmContent: betaUtms.utm_content, resultScore: result.overall_score,
      resultBand, targetMood: goal, occasion,
    });
  }, [betaSession, betaUtms, goal, occasion, result, resultBand, rewardClaim]);

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setErrorCode(''); setResult(null);
    try { setAsset(await prepareImageUploadAsset({ file, filenameBase: 'style-check', slot: 'outfit', source: 'file' })); }
    catch (error) { setAsset(null); setErrorCode(error?.code || 'image_prepare_failed'); }
  };

  const handleFaceImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setErrorCode(''); setResult(null);
    try { setFaceAsset(await prepareImageUploadAsset({ file, filenameBase: 'style-face', slot: 'face', source: 'file' })); }
    catch (error) { setFaceAsset(null); setErrorCode(error?.code || 'image_prepare_failed'); }
  };

  const analyze = async (event) => {
    event.preventDefault();
    if (!asset?.file || !faceAsset?.file || !goal || !occasion) { setErrorCode('required_fields'); return; }
    const form = new FormData(); form.append('image', asset.file); form.append('face_image', faceAsset.file); form.append('goal', goal); form.append('occasion', occasion); form.append('recommendation_mode', recommendationMode);
    setStatus('loading'); setErrorCode(''); setResult(null);
    try {
      const data = await apiFetchAuth('/api/outfit/analyze', { method: 'POST', body: form, headers: { 'Accept-Language': i18n.resolvedLanguage || i18n.language || 'ko' } });
      setResult(data); setActiveIndex(0); setStatus('success');
      const hasUsableResult = Boolean(data && (data.overall_summary || Number.isFinite(Number(data.overall_score)) || Array.isArray(data.items)));
      if (hasUsableResult) {
        trackEvent('result_viewed', {
          feature_type: 'style_check', source_page: 'style_check', entry_source: 'new_analysis',
        });
      }
    } catch (error) { setErrorCode(error?.code || 'api_request_failed'); setStatus('error'); }
  };

  const selectItem = (index, scroll) => {
    setActiveIndex(index);
    if (scroll && window.matchMedia('(max-width: 760px)').matches) cardRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const reset = () => { setResult(null); setStatus('idle'); setErrorCode(''); setActiveIndex(0); };
  const handleSurveyClick = (placement) => {
    trackEvent('style_beta_survey_click', {
      ...getStyleBetaSurveyAnalyticsParams({
      experimentId: STYLE_BETA_EXPERIMENT, utmSource: betaUtms.utm_source, utmMedium: betaUtms.utm_medium,
      utmCampaign: betaUtms.utm_campaign, utmContent: betaUtms.utm_content, resultBand, targetMood: goal, occasion,
      language: (i18n.resolvedLanguage || i18n.language || 'ko').startsWith('en') ? 'en' : 'ko',
      }),
      placement,
      reward_type: 'style_check_full_access_30d',
    });
  };
  const items = Array.isArray(result?.items) ? result.items : [];

  return (
    <div className="style-check-page">
      <header className="style-check-hero"><p>{t('style_check.eyebrow')}</p><h1>{t('style_check.title')}</h1><span>{t('style_check.description')}</span></header>
      {!result && status === 'loading' && <StyleAnalysisLoading imageSrc={asset?.previewUrl} t={t} />}
      {!result && status !== 'loading' && <form className="style-check-form" onSubmit={analyze}>
        <label className="style-check-upload"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImage} />
          {asset?.previewUrl ? <img src={asset.previewUrl} alt={t('style_check.upload.preview_alt')} /> : <div><strong>{t('style_check.upload.title')}</strong><span>{t('style_check.upload.help')}</span></div>}
        </label>
        <label className="style-check-upload style-check-face-upload"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFaceImage} />
          {faceAsset?.previewUrl ? <img src={faceAsset.previewUrl} alt={t('style_check.face_upload.preview_alt')} /> : <div><strong>{t('style_check.face_upload.title')}</strong><span>{t('style_check.face_upload.help')}</span></div>}
        </label>
        <fieldset><legend>{t('style_check.recommendation_mode.label')}</legend><div className="style-check-options">{['male', 'female', 'neutral'].map((value) => <button type="button" key={value} className={recommendationMode === value ? 'selected' : ''} onClick={() => setRecommendationMode(value)}>{t(`style_check.recommendation_mode.${value}`)}</button>)}</div></fieldset>
        <fieldset><legend>{t('style_check.goal.label')}</legend><div className="style-check-options">{goals.map((value) => <button type="button" key={value} className={goal === value ? 'selected' : ''} onClick={() => setGoal(value)}>{t(`style_check.goal.${value}`)}</button>)}</div></fieldset>
        <fieldset><legend>{t('style_check.occasion.label')}</legend><div className="style-check-options">{occasions.map((value) => <button type="button" key={value} className={occasion === value ? 'selected' : ''} onClick={() => setOccasion(value)}>{t(`style_check.occasion.${value}`)}</button>)}</div></fieldset>
        {!faceAsset?.file && <p className="style-check-face-required">{t('style_check.face_upload.required')}</p>}
        {errorCode && <p className="style-check-error">{t(`style_check.errors.${errorCode}`, t('style_check.errors.api_request_failed'))}</p>}
        <button className="style-check-submit" type="submit" disabled={status === 'loading' || !asset?.file || !faceAsset?.file || !goal || !occasion}>{status === 'loading' ? t('style_check.loading') : t('style_check.submit')}</button>
      </form>}

      {result && <section className="style-check-results">
        <div className="style-check-overall">
          <div className="style-check-overall-heading">
            <div className="style-check-overall-copy"><p>{t('style_check.result.overall')}</p><h2 title={result.overall_summary}>{compactCopy(result.overall_summary, 120)}</h2></div>
            <div className="style-check-score-wrap"><span>{t('style_check.result.score_label')}</span><div className="style-check-score">{result.overall_score}<small>/100</small></div></div>
          </div>
          <div className="style-check-summary-grid">{['outfit_quality', 'personal_fit', 'goal_fit'].map((type) => {
            const score = Math.max(0, Math.min(100, Number(result[type]?.score) || 0));
            return <article key={type}><div><span>{t(`style_check.summary.${type}`)}</span><strong>{score}<small>/100</small></strong></div><div className="style-check-summary-meter" aria-hidden="true"><i style={{ width: `${score}%` }} /></div><p title={result[type]?.summary}>{compactCopy(result[type]?.summary, 76)}</p></article>;
          })}</div>
        </div>
        <ResultQuickActions result={result} t={t} />
        {surveyUrl && <SurveyBenefitCard surveyUrl={surveyUrl} placement="after_summary" onClick={handleSurveyClick} t={t} />}
        <div id="style-check-details" className="style-result-details-anchor" />
        {result.current_style_summary && <section className="style-current-summary" aria-labelledby="current-style-summary-title">
          <p className="style-recommendation-eyebrow">{t('style_check.extension.summary.eyebrow')}</p>
          <h2 id="current-style-summary-title">{t('style_check.extension.summary.title')}</h2>
          <p>{result.current_style_summary}</p>
        </section>}
        <CurrentStyleHeading t={t} />
        {items.length > 0 && asset?.previewUrl ? <div className="style-check-inspector">
          <OutfitPhoto src={asset.previewUrl} items={items} activeIndex={activeIndex} onSelect={selectItem} t={t} />
          <div className="style-check-cards">{items.map((item, index) => <ItemCard key={`${item.category}-${index}`} item={item} index={index} active={activeIndex === index} onSelect={() => selectItem(index, false)} cardRef={(node) => { cardRefs.current[index] = node; }} t={t} />)}</div>
        </div> : <p className="style-check-empty">{t('style_check.result.empty')}</p>}
        <StyleRecommendationSections result={result} faceImageSrc={faceAsset?.previewUrl} t={t} />
        <StyleDiscoverySection
          items={result.discovery_items}
          market={safeMarket}
          marketResolved={marketResolved}
          isBeta={betaSession}
        />
        {surveyUrl && <SurveyBenefitCard surveyUrl={surveyUrl} placement="result_bottom" onClick={handleSurveyClick} t={t} />}
        <button type="button" className="style-check-again" onClick={reset}>{t('style_check.again')}</button>
      </section>}
    </div>
  );
}
